<template>
  <div
    class="relative min-h-screen bg-gradient-to-br from-white via-gray-50 to-logo-color/5 overflow-hidden"
  >
    <!-- Floating Shapes -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <!-- Large circle top right -->
      <div
        class="absolute -top-32 -right-32 w-96 h-96 bg-logo-color/10 rounded-full blur-3xl"
        data-aos="fade-left"
        data-aos-duration="1500"
      ></div>
      <!-- Medium circle bottom left -->
      <div
        class="absolute -bottom-20 -left-20 w-72 h-72 bg-logo-color/15 rounded-full blur-2xl"
        data-aos="fade-right"
        data-aos-duration="1500"
      ></div>
      <!-- Floating dots pattern -->
      <div
        class="absolute top-1/4 left-10 w-3 h-3 bg-logo-color/30 rounded-full animate-float"
      ></div>
      <div
        class="absolute top-1/3 right-20 w-4 h-4 bg-logo-color/20 rounded-full animate-float-delayed"
      ></div>
      <div
        class="absolute bottom-1/4 left-1/4 w-2 h-2 bg-logo-color/40 rounded-full animate-float"
      ></div>
      <div
        class="absolute top-2/3 right-1/3 w-3 h-3 bg-logo-color/25 rounded-full animate-float-delayed"
      ></div>
      <!-- Geometric shapes -->
      <div
        class="absolute top-20 right-1/4 w-8 h-8 border-2 border-logo-color/20 rotate-45 animate-spin-slow"
      ></div>
      <div
        class="absolute bottom-32 left-1/3 w-6 h-6 border-2 border-logo-color/15 rounded-full animate-pulse"
      ></div>
    </div>

    <div
      class="container mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-32 relative z-10"
    >
      <!-- Centered Hero Content -->
      <div class="text-center max-w-4xl mx-auto mb-16">
        <!-- Badge -->
        <div
          class="inline-flex items-center gap-3 px-6 py-2 bg-logo-color/10 rounded-full mb-8"
          data-aos="fade-down"
          data-aos-duration="800"
        >
          <span class="w-2 h-2 bg-logo-color rounded-full animate-pulse"></span>
          <span
            class="text-sm uppercase tracking-[0.2em] text-logo-color font-semibold"
          >
            Join Our Mission
          </span>
          <span class="w-2 h-2 bg-logo-color rounded-full animate-pulse"></span>
        </div>

        <!-- Main Heading with Gradient -->
        <h1
          class="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold mb-8 leading-[1.1]"
          data-aos="fade-up"
          data-aos-duration="1000"
        >
          <span class="text-logo-color">Be The</span>
          <br />
          <span
            class="bg-gradient-to-r from-logo-color via-logo-color/80 to-logo-color bg-clip-text text-transparent"
          >
            Change
          </span>
        </h1>

        <!-- Subtitle -->
        <p
          class="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed mb-12"
          data-aos="fade-up"
          data-aos-delay="200"
        >
          Join us in empowering young people across Africa. Your time,
          expertise, and support can transform lives and build ethical leaders
          for tomorrow.
        </p>

        <!-- CTA Button -->
        <div
          class="flex justify-center"
          data-aos="fade-up"
          data-aos-delay="400"
        >
          <a
            href="#ways-to-help"
            class="group px-8 py-4 bg-logo-color text-white rounded-full font-semibold hover:shadow-xl hover:shadow-logo-color/30 transition-all duration-300 flex items-center gap-3"
          >
            <span>See How You Can Help</span>
            <i
              class="fas fa-arrow-down group-hover:translate-y-1 transition-transform"
            ></i>
          </a>
        </div>
      </div>

      <!-- Impact Stats Cards -->
      <div
        class="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto"
        data-aos="fade-up"
        data-aos-delay="600"
      >
        <!-- Stat 1 -->
        <div class="group relative">
          <div
            class="absolute inset-0 bg-logo-color rounded-2xl transform rotate-3 group-hover:rotate-6 transition-transform duration-300"
          ></div>
          <div
            class="relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 transform group-hover:-translate-y-1 transition-all duration-300"
          >
            <div class="flex items-center gap-4">
              <div
                class="w-14 h-14 bg-logo-color/10 rounded-xl flex items-center justify-center"
              >
                <i class="fas fa-users text-2xl text-logo-color"></i>
              </div>
              <div>
                <div class="text-3xl font-bold text-logo-color">100+</div>
                <div class="text-sm text-gray-600">Active Volunteers</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Stat 2 -->
        <div class="group relative">
          <div
            class="absolute inset-0 bg-logo-color rounded-2xl transform -rotate-3 group-hover:-rotate-6 transition-transform duration-300"
          ></div>
          <div
            class="relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 transform group-hover:-translate-y-1 transition-all duration-300"
          >
            <div class="flex items-center gap-4">
              <div
                class="w-14 h-14 bg-logo-color/10 rounded-xl flex items-center justify-center"
              >
                <i
                  class="fas fa-chalkboard-teacher text-2xl text-logo-color"
                ></i>
              </div>
              <div>
                <div class="text-3xl font-bold text-logo-color">50+</div>
                <div class="text-sm text-gray-600">Expert Mentors</div>
              </div>
            </div>
          </div>
        </div>

        <!-- Stat 3 -->
        <div class="group relative">
          <div
            class="absolute inset-0 bg-logo-color rounded-2xl transform rotate-3 group-hover:rotate-6 transition-transform duration-300"
          ></div>
          <div
            class="relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 transform group-hover:-translate-y-1 transition-all duration-300"
          >
            <div class="flex items-center gap-4">
              <div
                class="w-14 h-14 bg-logo-color/10 rounded-xl flex items-center justify-center"
              >
                <i class="fas fa-heart text-2xl text-logo-color"></i>
              </div>
              <div>
                <div class="text-3xl font-bold text-logo-color">500+</div>
                <div class="text-sm text-gray-600">Lives Impacted</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "InvolvedIntro",
};
</script>

<style scoped>
@keyframes float {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-20px);
  }
}

@keyframes float-delayed {
  0%,
  100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-15px);
  }
}

@keyframes spin-slow {
  from {
    transform: rotate(45deg);
  }
  to {
    transform: rotate(405deg);
  }
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animate-float-delayed {
  animation: float-delayed 8s ease-in-out infinite;
  animation-delay: 1s;
}

.animate-spin-slow {
  animation: spin-slow 20s linear infinite;
}
</style>
